// ---------------------------------------------------------------------------
// CRM-D Observability — Metrics Pipeline CQRS Event Catalog
// (vocabulary tier — Phase 2 follow-up to the CRM-D packet)
// ---------------------------------------------------------------------------
// Source story: `docs/vision_execution/openclaw/CRM-D-OBSERVABILITY-LOGGING-
// RECHECK-PACKET.md` — CRM-D recheck enumerates remaining observability
// gaps the runtime-observability catalog (`crm-d-runtime-observability-
// event-catalog.ts`) did NOT close. That sibling catalog covers the
// REQUEST-side surface (per-route timing, SLO breach, request-side
// health rollup). It is silent on the METRICS-PIPELINE surface — i.e.
// the ClickHouse materialized-view (MV) refresh lifecycle that produces
// the inputs the request-side rollup reads from.
//
// Residual gap (request-side catalog does NOT close):
//   "Metrics-pipeline events — MV materialization completion, staleness
//    detection on the projection itself, and refresh-cycle completion —
//    are not catalogued. Without them, on-call cannot distinguish a stale
//    `crm_route_timing_mv` caused by a slow ingest run from one caused
//    by a hung projection."
//
// What this file is NOT:
//  - It does not change the request-side `crm.runtime_observability.*`
//    event family. That family stays request-scoped (per-route latency,
//    SLO breach, per-request status, request-side rollup).
//  - It does not change CRM-020 `crm.write_audit.*` (audit-event family).
//  - It does not ship a new emitter, route, or migration. Phase 2
//    catalogues the closed vocabulary so the metrics-pipeline worker
//    + the materializer ships against a closed list.
//
// What it IS:
//  - The single source of truth for the THREE metrics-pipeline event
//    type strings covering the projection lifecycle:
//      * `crm.metrics.materialized@v1`        — MV materialization SUCCEEDED
//      * `crm.metrics.staleness_detected@v1`  — projection itself is stale
//      * `crm.metrics.refresh_completed@v1`   — refresh cycle finished
//        (one fact per cycle; carries summary verdict)
//  - Closed vocabulary for the four refresh-target surfaces (mirrors
//    the freshness-source vocab on the request-side catalog so a
//    metrics-pipeline event lines up 1:1 with the request-side rollup
//    surface it feeds).
//  - Closed vocabulary for the three refresh-completion verdicts
//    (`success` / `partial` / `failed`) the materializer reports.
//  - Closed vocabulary for the four staleness-severity tiers
//    (`info` / `warn` / `breach` / `outage`) so on-call routing keys
//    off a typed code instead of free-form text.
//  - Pure builders (no I/O, no DB writes, no env access) that accept
//    caller primitives + an optional `now: Date` injection point and
//    return a typed, frozen, Zod-validated payload with an ISO-8601
//    canonical `occurredAtIso` timestamp.
//  - Runtime narrowing guards so DLQ / audit-runner / `/health` rollup
//    reject unknown event types at the boundary.
//
// Per Governance Rule #7 ("Event Catalog"): every event type MUST be
// catalogued BEFORE implementation. This file locks the closed
// vocabulary so the metrics-pipeline materializer + the staleness
// detector ship against a closed list.
//
// Mirrors the AN-024 / RV-006 / MKT-016 / EM-013 / DOM-007 / MKT-008 /
// RV-015 / AN-013 / INB-017 / AG-024 / ONB-006 / ONB-010 / CRM-018 /
// CRM-020 / CRM-D-Runtime Phase 2 catalog pattern.
// ---------------------------------------------------------------------------

import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Event-type catalog                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of CRM-D metrics-pipeline event types. Versioned with
 * `@v1` suffix per platform convention (Governance Rule #7).
 *
 * One business fact per event (Governance Rule #6):
 *  - `crm.metrics.materialized@v1` — a single MV materialization run
 *    completed successfully and wrote `rowsWritten` rows covering
 *    `[windowStartIso, windowEndIso]`. Emitted by the materializer
 *    after the CH `INSERT … SELECT` commits. Drives the
 *    request-side `freshness_source` clock for that MV.
 *  - `crm.metrics.staleness_detected@v1` — the materializer detected
 *    that a refresh target's projection is stale beyond its
 *    `freshnessBudgetSeconds`. Distinct from the request-side
 *    `freshness_lagged@v1` because this is emitted DURING the refresh
 *    decision (input to the scheduler), not as a downstream observation.
 *  - `crm.metrics.refresh_completed@v1` — a complete refresh cycle
 *    (one full pass over all enabled refresh targets) finished. Carries
 *    summary verdict (`success` / `partial` / `failed`) + counts so the
 *    cron / queue runner can decide whether to back off or re-trigger.
 */
export const CRM_D_METRICS_EVENT_TYPES = [
  "crm.metrics.materialized@v1",
  "crm.metrics.staleness_detected@v1",
  "crm.metrics.refresh_completed@v1",
] as const;

export type CrmDMetricsEventType = (typeof CRM_D_METRICS_EVENT_TYPES)[number];

/* -------------------------------------------------------------------------- */
/* Closed vocabularies                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of refresh-target surfaces. Mirrors the request-side
 * `CRM_D_RUNTIME_FRESHNESS_SOURCE_VOCAB` on
 * `crm-d-runtime-observability-event-catalog.ts` so a metrics-pipeline
 * event lines up 1:1 with the request-side rollup surface it feeds.
 *
 * Drift between this vocab and the request-side freshness-source vocab
 * is caught by the `mirrors-runtime-freshness-source-vocab` proof test.
 * Adding a new MV MUST land an entry in BOTH catalogs in the same commit.
 */
/**
 * Per-MV refresh targets. Used by `materialized` + `staleness_detected`
 * facts (these are per-MV, not cycle-level).
 */
export const CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB = [
  "crm_route_timing_mv",
  "crm_route_error_rate_mv",
  "crm_route_freshness_mv",
  "crm_route_slo_mv",
] as const;

/**
 * Full refresh-target vocabulary including the cycle-level sentinel.
 *
 * The `cycle` sentinel is emitted ONLY by
 * `crm.metrics.refresh_completed@v1` to mark a refresh-cycle banner row
 * that aggregates multiple per-MV targets. Per-MV facts (materialized,
 * staleness_detected) MUST use a value from
 * `CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB` instead.
 *
 * Drift between this vocab and the request-side freshness-source vocab
 * is caught by the `mirrors-runtime-freshness-source-vocab` proof test.
 * Adding a new MV MUST land an entry in BOTH catalogs in the same commit.
 */
export const CRM_D_METRICS_REFRESH_TARGET_VOCAB = [
  ...CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB,
  "cycle",
] as const;

export type CrmDMetricsRefreshTargetVocab =
  (typeof CRM_D_METRICS_REFRESH_TARGET_VOCAB)[number];

export type CrmDMetricsRefreshTargetPerMvVocab =
  (typeof CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB)[number];

/**
 * @deprecated since 2026-05-17. Kept as an alias for one release so
 * downstream tenants pinned to the pre-`cycle` rename do not break.
 * New callers MUST use `CRM_D_METRICS_REFRESH_TARGET_VOCAB` directly.
 */
export const CRM_D_METRICS_REFRESH_COMPLETED_TARGET_VOCAB =
  CRM_D_METRICS_REFRESH_TARGET_VOCAB;

export type CrmDMetricsRefreshCompletedTargetVocab =
  (typeof CRM_D_METRICS_REFRESH_COMPLETED_TARGET_VOCAB)[number];

/**
 * Closed catalog of refresh-completion verdicts. Drives the cron / queue
 * runner's back-off decision.
 *
 *  - `success` — all enabled refresh targets materialized within budget.
 *  - `partial` — at least one target materialized AND at least one
 *    target either failed or was skipped. Runner re-triggers the failed
 *    set on the next tick.
 *  - `failed`  — every enabled target failed (or no target ran at all).
 *    Runner emits a higher-severity alert and pages on-call.
 */
export const CRM_D_METRICS_REFRESH_VERDICT_VOCAB = [
  "success",
  "partial",
  "failed",
] as const;

export type CrmDMetricsRefreshVerdictVocab =
  (typeof CRM_D_METRICS_REFRESH_VERDICT_VOCAB)[number];

/**
 * Closed catalog of staleness severity tiers. On-call routing keys off
 * the tier so a slow ingest does not page the same way as a hung
 * projection.
 *
 *  - `info`    — observed lag is below the warn budget. Carried for
 *                trend telemetry; no alert fires.
 *  - `warn`    — observed lag exceeds the warn budget but is below the
 *                breach budget. Status page renders amber.
 *  - `breach`  — observed lag exceeds the breach budget. Status page
 *                renders red; on-call paged.
 *  - `outage`  — observed lag exceeds the outage budget (typically
 *                >= 4x the breach budget). On-call paged at P1.
 */
export const CRM_D_METRICS_STALENESS_SEVERITY_VOCAB = [
  "info",
  "warn",
  "breach",
  "outage",
] as const;

export type CrmDMetricsStalenessSeverityVocab =
  (typeof CRM_D_METRICS_STALENESS_SEVERITY_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Runtime narrowing guard. DLQ consumers, the audit-runner, and the
 * `/health` rollup use this to reject unknown event types at the
 * boundary instead of silently fanning them downstream.
 */
export function isCrmDMetricsEventType(
  value: unknown,
): value is CrmDMetricsEventType {
  return (
    typeof value === "string" &&
    (CRM_D_METRICS_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isCrmDMetricsRefreshTargetVocab(
  value: unknown,
): value is CrmDMetricsRefreshTargetVocab {
  return (
    typeof value === "string" &&
    (CRM_D_METRICS_REFRESH_TARGET_VOCAB as readonly string[]).includes(value)
  );
}

export function isCrmDMetricsRefreshCompletedTargetVocab(
  value: unknown,
): value is CrmDMetricsRefreshCompletedTargetVocab {
  return (
    typeof value === "string" &&
    (CRM_D_METRICS_REFRESH_COMPLETED_TARGET_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

export function isCrmDMetricsRefreshVerdictVocab(
  value: unknown,
): value is CrmDMetricsRefreshVerdictVocab {
  return (
    typeof value === "string" &&
    (CRM_D_METRICS_REFRESH_VERDICT_VOCAB as readonly string[]).includes(value)
  );
}

export function isCrmDMetricsStalenessSeverityVocab(
  value: unknown,
): value is CrmDMetricsStalenessSeverityVocab {
  return (
    typeof value === "string" &&
    (CRM_D_METRICS_STALENESS_SEVERITY_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

/* -------------------------------------------------------------------------- */
/* Shared base                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Common identity fields every metrics-pipeline event carries. The
 * `/health` rollup + downstream consumers group by
 * (`tenantId`, `refreshTarget`) to reconstruct per-tenant per-MV state.
 *
 * `tenantId` is REQUIRED even though refresh is typically platform-wide:
 * per-tenant materialization fencing is supported (a single tenant's
 * partition can be slow without the platform being slow).
 *
 * `correlationId` is derived per-event (see `derive*CorrelationId`
 * helpers) so the reconciler can join across `materialized`,
 * `staleness_detected`, and `refresh_completed` for the same cycle.
 */
export interface CrmDMetricsEventBase {
  tenantId: string;
  refreshTarget: CrmDMetricsRefreshTargetVocab;
  occurredAtIso: string;
  correlationId: string;
}

/* -------------------------------------------------------------------------- */
/* Zod payload schemas + types                                                */
/* -------------------------------------------------------------------------- */

export const CrmDMetricsMaterializedPayloadSchema = z.object({
  type: z.literal("crm.metrics.materialized@v1"),
  tenantId: z.string().min(1),
  /**
   * Per-MV target only — the `cycle` sentinel is reserved for
   * `refresh_completed@v1` banner rows and would erase MV-level
   * attribution if it landed here.
   */
  refreshTarget: z.enum(CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB),
  /** ISO-8601 — when the materialization commit finished. */
  occurredAtIso: z.string().datetime({ offset: true }),
  /** ISO-8601 — inclusive lower bound of the materialized window. */
  windowStartIso: z.string().datetime({ offset: true }),
  /** ISO-8601 — exclusive upper bound of the materialized window. */
  windowEndIso: z.string().datetime({ offset: true }),
  /** Rows written by this materialization run. >= 0. */
  rowsWritten: z.number().int().nonnegative(),
  /** Duration of the CH `INSERT … SELECT` in milliseconds. >= 0. */
  durationMs: z.number().int().nonnegative(),
  correlationId: z.string().min(1),
});

export type CrmDMetricsMaterializedPayload = z.infer<
  typeof CrmDMetricsMaterializedPayloadSchema
>;

export const CrmDMetricsStalenessDetectedPayloadSchema = z.object({
  type: z.literal("crm.metrics.staleness_detected@v1"),
  tenantId: z.string().min(1),
  /**
   * Per-MV target only — staleness is detected per-MV, not at the
   * cycle granularity.
   */
  refreshTarget: z.enum(CRM_D_METRICS_REFRESH_TARGET_PER_MV_VOCAB),
  /** ISO-8601 — when the materializer detected the staleness. */
  occurredAtIso: z.string().datetime({ offset: true }),
  /** Closed severity vocab — drives on-call routing severity. */
  severity: z.enum(CRM_D_METRICS_STALENESS_SEVERITY_VOCAB),
  /** Observed lag in seconds (now - max(written_at)). >= 0. */
  observedLagSeconds: z.number().int().nonnegative(),
  /** Staleness budget in seconds the observed lag crossed. >= 0. */
  budgetSeconds: z.number().int().nonnegative(),
  correlationId: z.string().min(1),
});

export type CrmDMetricsStalenessDetectedPayload = z.infer<
  typeof CrmDMetricsStalenessDetectedPayloadSchema
>;

export const CrmDMetricsRefreshCompletedPayloadSchema = z.object({
  type: z.literal("crm.metrics.refresh_completed@v1"),
  tenantId: z.string().min(1),
  /**
   * The refresh-cycle banner row uses the `crm_metrics_refresh_cycle`
   * sentinel to indicate the event covers MULTIPLE targets. Per-target
   * verdicts ride on prior `materialized` / `staleness_detected` events.
   */
  refreshTarget: z.enum(CRM_D_METRICS_REFRESH_COMPLETED_TARGET_VOCAB),
  /** ISO-8601 — when the refresh cycle finished. */
  occurredAtIso: z.string().datetime({ offset: true }),
  /** Closed verdict vocab — drives runner back-off / re-trigger. */
  verdict: z.enum(CRM_D_METRICS_REFRESH_VERDICT_VOCAB),
  /** Targets successfully materialized in this cycle. >= 0. */
  targetsSucceeded: z.number().int().nonnegative(),
  /** Targets that failed during this cycle. >= 0. */
  targetsFailed: z.number().int().nonnegative(),
  /** Total duration of the cycle in milliseconds. >= 0. */
  durationMs: z.number().int().nonnegative(),
  correlationId: z.string().min(1),
}).superRefine((payload, ctx) => {
  const { verdict, targetsSucceeded, targetsFailed } = payload;
  // Strict three-way invariant — every verdict pins BOTH success and
  // failure counts. The `failed` branch additionally REQUIRES at least
  // one observed failure so an empty cycle (succeeded=0 AND failed=0)
  // cannot masquerade as a deliberate failure. Empty cycles are a
  // runner bug, not a refresh outcome — they should surface as a
  // separate alert, not as a `failed` event.
  const valid =
    (verdict === "success" && targetsSucceeded > 0 && targetsFailed === 0) ||
    (verdict === "partial" && targetsSucceeded > 0 && targetsFailed > 0) ||
    (verdict === "failed" && targetsSucceeded === 0 && targetsFailed > 0);

  if (!valid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["verdict"],
      message:
        "refresh_completed verdict must match targetsSucceeded/targetsFailed " +
        "(success: succeeded>0 failed=0; partial: succeeded>0 failed>0; " +
        "failed: succeeded=0 failed>0)",
    });
  }
});

export type CrmDMetricsRefreshCompletedPayload = z.infer<
  typeof CrmDMetricsRefreshCompletedPayloadSchema
>;

/* -------------------------------------------------------------------------- */
/* Discriminated union                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Consumers (audit-runner, DLQ, `/health` rollup, briefing engine)
 * can `switch (event.type)` and pull the right payload shape without
 * any-casting.
 */
export type CrmDMetricsEvent =
  | CrmDMetricsMaterializedPayload
  | CrmDMetricsStalenessDetectedPayload
  | CrmDMetricsRefreshCompletedPayload;

/* -------------------------------------------------------------------------- */
/* Correlation-id derivation                                                  */
/* -------------------------------------------------------------------------- */
// Each event type pins its own deterministic correlation root so the
// reconciler can join across `materialized`, `staleness_detected`, and
// `refresh_completed` for the same tenant + cycle.
//
//   materialized           → `crm-metrics:{refreshTarget}:{windowEndIso}`
//   staleness_detected     → `crm-metrics-staleness:{refreshTarget}:{occurredAtIso}`
//   refresh_completed      → `crm-metrics-refresh:{tenantId}:{occurredAtIso}`

export function deriveMaterializedCorrelationId(
  refreshTarget: CrmDMetricsRefreshTargetVocab,
  windowEndIso: string,
): string {
  if (!refreshTarget) {
    throw new Error(
      "deriveMaterializedCorrelationId: refreshTarget required",
    );
  }
  if (!windowEndIso) {
    throw new Error("deriveMaterializedCorrelationId: windowEndIso required");
  }
  return `crm-metrics:${refreshTarget}:${windowEndIso}`;
}

export function deriveStalenessDetectedCorrelationId(
  refreshTarget: CrmDMetricsRefreshTargetVocab,
  occurredAtIso: string,
): string {
  if (!refreshTarget) {
    throw new Error(
      "deriveStalenessDetectedCorrelationId: refreshTarget required",
    );
  }
  if (!occurredAtIso) {
    throw new Error(
      "deriveStalenessDetectedCorrelationId: occurredAtIso required",
    );
  }
  return `crm-metrics-staleness:${refreshTarget}:${occurredAtIso}`;
}

export function deriveRefreshCompletedCorrelationId(
  tenantId: string,
  occurredAtIso: string,
): string {
  if (!tenantId) {
    throw new Error("deriveRefreshCompletedCorrelationId: tenantId required");
  }
  if (!occurredAtIso) {
    throw new Error(
      "deriveRefreshCompletedCorrelationId: occurredAtIso required",
    );
  }
  return `crm-metrics-refresh:${tenantId}:${occurredAtIso}`;
}

/* -------------------------------------------------------------------------- */
/* Pure builders                                                              */
/* -------------------------------------------------------------------------- */
// No I/O. No DB writes. No env access. The writer (cron / queue runner
// owning the metrics-pipeline) owns actual emission to `customer_events`
// + the `event-router` worker — mirrors the BIL-017 / AN-001 / MKT-001
// Phase 3 pattern.

function assertNonEmpty(value: string, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`CRM-D metrics builder: ${field} must be non-empty string`);
  }
  return value;
}

function assertNonNegativeInteger(value: number, field: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new Error(
      `CRM-D metrics builder: ${field} must be non-negative integer (got ${value})`,
    );
  }
  return value;
}

/**
 * Canonical ISO-8601 with offset. Accepts an optional `now: Date`
 * injection point so tests can pin time without mocking `Date`.
 */
function nowIso(now: Date | undefined): string {
  return (now ?? new Date()).toISOString();
}

/**
 * Canonicalises a caller-supplied timestamp. Accepts `Date` or a
 * pre-formatted ISO-8601 string and round-trips through `Date` to
 * normalise to UTC-Z form. Throws on invalid input.
 */
function canonicalIso(value: Date | string, field: string): string {
  const asDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(asDate.getTime())) {
    throw new Error(
      `CRM-D metrics builder: ${field} must be a valid Date or ISO-8601 string`,
    );
  }
  return asDate.toISOString();
}

export interface BuildMetricsMaterializedInput {
  tenantId: string;
  refreshTarget: CrmDMetricsRefreshTargetVocab;
  windowStart: Date | string;
  windowEnd: Date | string;
  rowsWritten: number;
  durationMs: number;
  now?: Date;
}

export function buildMetricsMaterializedEvent(
  input: BuildMetricsMaterializedInput,
): CrmDMetricsMaterializedPayload {
  const windowEndIso = canonicalIso(input.windowEnd, "windowEnd");
  return CrmDMetricsMaterializedPayloadSchema.parse({
    type: "crm.metrics.materialized@v1",
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    refreshTarget: input.refreshTarget,
    occurredAtIso: nowIso(input.now),
    windowStartIso: canonicalIso(input.windowStart, "windowStart"),
    windowEndIso,
    rowsWritten: assertNonNegativeInteger(input.rowsWritten, "rowsWritten"),
    durationMs: assertNonNegativeInteger(input.durationMs, "durationMs"),
    correlationId: deriveMaterializedCorrelationId(
      input.refreshTarget,
      windowEndIso,
    ),
  });
}

export interface BuildMetricsStalenessDetectedInput {
  tenantId: string;
  refreshTarget: CrmDMetricsRefreshTargetVocab;
  severity: CrmDMetricsStalenessSeverityVocab;
  observedLagSeconds: number;
  budgetSeconds: number;
  now?: Date;
}

export function buildMetricsStalenessDetectedEvent(
  input: BuildMetricsStalenessDetectedInput,
): CrmDMetricsStalenessDetectedPayload {
  const occurredAtIso = nowIso(input.now);
  return CrmDMetricsStalenessDetectedPayloadSchema.parse({
    type: "crm.metrics.staleness_detected@v1",
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    refreshTarget: input.refreshTarget,
    occurredAtIso,
    severity: input.severity,
    observedLagSeconds: assertNonNegativeInteger(
      input.observedLagSeconds,
      "observedLagSeconds",
    ),
    budgetSeconds: assertNonNegativeInteger(
      input.budgetSeconds,
      "budgetSeconds",
    ),
    correlationId: deriveStalenessDetectedCorrelationId(
      input.refreshTarget,
      occurredAtIso,
    ),
  });
}

export interface BuildMetricsRefreshCompletedInput {
  tenantId: string;
  refreshTarget: CrmDMetricsRefreshCompletedTargetVocab;
  verdict: CrmDMetricsRefreshVerdictVocab;
  targetsSucceeded: number;
  targetsFailed: number;
  durationMs: number;
  now?: Date;
}

export function buildMetricsRefreshCompletedEvent(
  input: BuildMetricsRefreshCompletedInput,
): CrmDMetricsRefreshCompletedPayload {
  const occurredAtIso = nowIso(input.now);
  return CrmDMetricsRefreshCompletedPayloadSchema.parse({
    type: "crm.metrics.refresh_completed@v1",
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    refreshTarget: input.refreshTarget,
    occurredAtIso,
    verdict: input.verdict,
    targetsSucceeded: assertNonNegativeInteger(
      input.targetsSucceeded,
      "targetsSucceeded",
    ),
    targetsFailed: assertNonNegativeInteger(
      input.targetsFailed,
      "targetsFailed",
    ),
    durationMs: assertNonNegativeInteger(input.durationMs, "durationMs"),
    correlationId: deriveRefreshCompletedCorrelationId(
      input.tenantId,
      occurredAtIso,
    ),
  });
}
